import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createClasses,
  Tab,
  Tabs,
  TransparentButton,
} from "@bloomberg/media-ui-core";
import { Change, Variant } from "@bloomberg/media-ui-markets";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import WatchlistDashboardTitle from "../WatchlistDashboardTitle";
import styles from "./styles.module.css";
import { prepareHeatmapTiles, type WatchlistHeatmapPosition } from "./util";
import Heatmap from "~components/Heatmap";
import HeatmapLegend from "~components/WatchlistHeatmap/HeatmapLegend";
import {
  useCurrentWatchlist,
  useIsLoading,
  useIsHeatmapMaximized,
  useHeatmapTabView,
  useWatchlistActions,
  WatchlistContext,
} from "~context/WatchlistContext";
import { formatPriceLabel } from "~utils/portfolio/formatNumber";
import { formatValue } from "~utils/section-front/formatChange";
import { getCurrencySymbol } from "~utils/portfolio/getCurrencySymbol";
import type { HeatmapTile } from "~types/types";

const DASHBOARD_V2_FEEDBACK_URL =
  "https://www.bloomberg.com/feedback?page=watchlist-heatmap";

// Tab constants
const TAB_PERFORMANCE = "Performance";
const TAB_HOLDINGS_SIZE = "Holdings Size";

// Dimension constants (match V1 behavior)
const MINIMIZED_HEIGHT = 280;
const MAXIMIZED_HEIGHT = 600;

export default function HeatmapWrapper() {
  const store = useContext(WatchlistContext);
  const currentWatchlist = useCurrentWatchlist();
  const isLoading = useIsLoading();
  const isMaximized = useIsHeatmapMaximized();
  const heatmapTabView = useHeatmapTabView();
  const actions = useWatchlistActions();

  // Track container width for responsive heatmap
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Default width
  const [width, setWidth] = useState(600);

  // Calculate selected tab title from state
  const selectedTab =
    heatmapTabView === "PERF" ? TAB_PERFORMANCE : TAB_HOLDINGS_SIZE;

  // Handle tab switch
  const handleTabChange = useCallback(
    (tabTitle: string) => {
      const newTab = tabTitle === TAB_PERFORMANCE ? "PERF" : "HOLD";
      const eventLabel =
        newTab === "PERF" ? "performance_view" : "holdings_size_view";

      trackClickEvent("watchlistHeatmap", eventLabel, "click", "watchlist");
      actions.setHeatmapTab(newTab);
    },
    [actions]
  );

  // Handle maximization toggle
  const toggleView = useCallback(() => {
    if (!store) return;

    const nextDashboardView = isMaximized ? "DASH" : "HTMP";
    const clickEvent = isMaximized ? "watchlistHide" : "watchlistShow";
    const eventLabel = isMaximized
      ? "minimize_view_heatmap"
      : "expand_view_heatmap";

    trackClickEvent(clickEvent, eventLabel, "click", "watchlist");

    const { updateUserPreferences } = store.getState();

    void updateUserPreferences({ dashboardView: nextDashboardView });
  }, [isMaximized, store]);

  // Track width changes
  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;

        if (newWidth > 0) {
          setWidth(newWidth);
        }
      }
    });

    observer.observe(containerRef.current);

    // Set initial width
    const initialWidth = containerRef.current.offsetWidth;

    if (initialWidth > 0) {
      setWidth(initialWidth);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const renderHoverContent = useCallback(
    (tile: HeatmapTile<WatchlistHeatmapPosition>) => (
      <>
        <div
          className={createClasses(styles.hoverContent, styles.tickerSymbol)}
        >
          {tile.data.ticker}
        </div>
        <div
          className={createClasses(
            styles.hoverContent,
            tile.data.shares >= 1 ? styles.tickerSymbol : styles.tickerName
          )}
        >
          {tile.data.longCompName}
        </div>
        <div className={styles.hoverContent}>
          {getCurrencySymbol(tile.data.currency)} {formatValue(tile.data.price)}
        </div>
        {tile.data.dayPCTChange !== 0 && tile.data.priceChange1Day !== 0 && (
          <div className={styles.hoverContent}>
            <Change
              name="change"
              percentChange={tile.data.dayPCTChange}
              priceChange={tile.data.priceChange1Day}
              size="large"
              variant={Variant.refresh2024}
            />
          </div>
        )}
        {tile.data.shares > 0 && (
          <div className={styles.hoverContent}>
            {formatValue(tile.data.shares, 2, 0)}{" "}
            {tile.data.shares > 1 ? "Shares" : "Share"}
          </div>
        )}
      </>
    ),
    []
  );

  // Prepare tile data from current watchlist
  const tiles = useMemo(() => {
    if (!currentWatchlist?.positions) return [];

    return prepareHeatmapTiles(currentWatchlist.positions, heatmapTabView).map(
      (tile) => ({
        ...tile,
        content: (
          <div className={styles.percentChange}>
            {formatPriceLabel(tile.dayPCTChange)}
            <span className={styles.percentSymbol}>%</span>
          </div>
        ),
      })
    );
  }, [currentWatchlist?.positions, heatmapTabView]);

  // Calculate dimensions
  const height = isMaximized ? MAXIMIZED_HEIGHT : MINIMIZED_HEIGHT;

  // Container class with maximization state
  const cx = createClasses([
    styles.heatmapContainer,
    {
      [styles.maximized]: isMaximized,
    },
  ]);

  return (
    <div ref={containerRef} className={cx}>
      <WatchlistDashboardTitle
        title="1D Heatmap"
        description="View your watchlist as a treemap colored by 1-day performance. Tile size represents market value."
        feedbackUrl={DASHBOARD_V2_FEEDBACK_URL}
        feedbackTrackingEventLabel="survey_heatmap"
        tooltipOpenEventLabel="tooltip_heatmap_open"
        tooltipCloseEventLabel="tooltip_heatmap_close"
      >
        <TransparentButton
          size="small"
          className={styles.seeMoreLessButton}
          onClick={toggleView}
        >
          {isMaximized ? "See Less" : "See More"}
        </TransparentButton>
      </WatchlistDashboardTitle>

      <div className={styles.tabsContainer}>
        <Tabs
          className={styles.tabs}
          variant="gliding"
          onTabSwitch={handleTabChange}
        >
          {[TAB_PERFORMANCE, TAB_HOLDINGS_SIZE].map((tabTitle) => (
            <Tab
              key={tabTitle}
              title={tabTitle}
              isActive={selectedTab === tabTitle}
              isDefault={selectedTab === tabTitle}
            >
              <></>
            </Tab>
          ))}
        </Tabs>
        {isMaximized && <HeatmapLegend />}
      </div>

      <div className={styles.heatmapContent}>
        {isLoading && (
          <div className={styles.placeholder}>
            <div className={styles.shimmer} style={{ height }} />
          </div>
        )}
        {!isLoading && tiles.length === 0 && (
          <div className={styles.emptyState} style={{ height }}>
            <p>No positions with market value to display</p>
          </div>
        )}
        {!isLoading && tiles.length > 0 && (
          <Heatmap<WatchlistHeatmapPosition>
            tilesData={tiles}
            width={width}
            height={height}
            enableTileFocusEvents
            renderHoverContent={renderHoverContent}
          />
        )}
      </div>
    </div>
  );
}
